import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { DisplayGridColumnSettings } from 'src/app/models/gridColumnSettings.model';
import { ColumnFilter, DrillDownFilterChip, FilterOperator, QuickFilter } from 'src/app/models/columnFilter.model';
import { OperatorTypeEnum } from 'src/app/enums/operatorType.enum';
import { GridView } from 'src/app/models/gridView.model';
import { Lookup } from 'src/app/models/lookup.model';
import { catchError, of, forkJoin, tap, switchMap, map, Observable } from 'rxjs';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { UnitFilterService, UnitOption } from 'src/app/services/unit-filter.service';
import { buildOperatorKey } from 'src/app/shared/utils/filter-key.util';
import { DATE_FILTER_TYPE_ENUM } from 'src/app/shared/grid/baseGrid.component';
import { TranslateService } from 'src/app/services/translate.service';

@Injectable({ providedIn: 'root' })
export class GridSettingsService {
    objectOptions: { [property: string]: Lookup[] } = {};
    private readonly resolvedFallbacks: { [property: string]: Lookup[] } = {};
    constructor(
        public dateFormatService: DateFormatService,
        private unitFilterService: UnitFilterService,
        private translateService: TranslateService
    ) { }

    public readonly dateOperators = [
        { value: FilterOperator.Equal, label: 'is' },
        { value: FilterOperator.LessThan, label: 'is before' },
        { value: FilterOperator.GreaterThan, label: 'is after' },
        { value: FilterOperator.LessThanOrEqual, label: 'is on or before' },
        { value: FilterOperator.GreaterThanOrEqual, label: 'is on or after' },
        { value: FilterOperator.Between, label: 'is between' },
        { value: FilterOperator.RelativeToToday, label: 'is relative to today' },
        { value: FilterOperator.IsEmpty, label: 'is empty' },
        { value: FilterOperator.IsNotEmpty, label: 'is not empty' }
    ];

    public readonly numberOperators = [
        { value: FilterOperator.Equal, label: 'is' },
        { value: FilterOperator.LessThan, label: 'is less than' },
        { value: FilterOperator.GreaterThan, label: 'is more than' },
        { value: FilterOperator.LessThanOrEqual, label: 'is less than or equal to' },
        { value: FilterOperator.GreaterThanOrEqual, label: 'is more than or equal to' },
        { value: FilterOperator.Between, label: 'is between' },
        { value: FilterOperator.IsEmpty, label: 'is empty' },
        { value: FilterOperator.IsNotEmpty, label: 'is not empty' }
    ];

    public readonly stringOperators = [
        { value: FilterOperator.Equal, label: 'is' },
        { value: FilterOperator.Contains, label: 'contains' },
        { value: FilterOperator.StartsWith, label: 'starts with' },
        { value: FilterOperator.IsEmpty, label: 'is empty' },
        { value: FilterOperator.IsNotEmpty, label: 'is not empty' }
    ];

    populateObjectOptions(gridViewColumns: DisplayGridColumnSettings[], activeFilters: ColumnFilter[], callback?: () => void): void {
        const columnsWithService = gridViewColumns.filter(col => col.objectMap?.service);

        if (columnsWithService.length === 0) {
            callback?.();
            return;
        }

        const requests = columnsWithService.map(col =>
            col.objectMap!.service.getAll('').pipe(
                catchError(err => {
                    return of([] as Lookup[]);
                })
            )
        );

        forkJoin(requests).pipe(
            tap((results: Lookup[][]) => {
                columnsWithService.forEach((col, index) => {
                    const fallbacks = this.resolvedFallbacks[col.property] ?? [];
                    const bulkIds = new Set(results[index].map(o => String(o.id)));
                    this.objectOptions[col.property] = [...results[index], ...fallbacks.filter(o => !bulkIds.has(String(o.id)))];
                });
            }),
            switchMap(() => this.resolveMissingObjectOptions(columnsWithService, activeFilters))
        ).subscribe({
            next: () => callback?.(),
            error: () => callback?.()
        });
    }

    private resolveMissingObjectOptions(
        columnsWithService: DisplayGridColumnSettings[],
        activeFilters: ColumnFilter[]
    ): Observable<void> {
        const columnByProperty = new Map(columnsWithService.map(col => [col.property, col]));
        const missingIdsByProperty = new Map<string, Set<string>>();

        activeFilters.forEach(filter => {
            const column = columnByProperty.get(filter.column.property);
            if (!column || !filter.valueArray) return;

            const resolvedIds = this.objectOptions[column.property] ?? [];
            filter.valueArray.forEach(value => {
                const id = String(value);
                if (resolvedIds.some(o => String(o.id) === id)) return;

                const ids = missingIdsByProperty.get(column.property) ?? new Set<string>();
                ids.add(id);
                missingIdsByProperty.set(column.property, ids);
            });
        });

        if (missingIdsByProperty.size === 0) {
            return of(undefined);
        }

        const properties = Array.from(missingIdsByProperty.keys());
        const requests = properties.map(property => {
            const idsByProperty = Array.from(missingIdsByProperty.get(property)!);
            const filterBy = JSON.stringify({
                OperatorTypeEnum: JSON.stringify({
                    Condition: OperatorTypeEnum.Equal,
                    Field: 'id',
                    Value: idsByProperty.join(', ')
                })
            });

            return columnByProperty.get(property)!.objectMap!.service.getAll(filterBy).pipe(
                catchError(() => of([] as Lookup[]))
            );
        });

        return forkJoin(requests).pipe(
            tap(results => {
                properties.forEach((property, index) => {
                    const requestedIds = missingIdsByProperty.get(property)!;
                    const resolved = results[index];
                    const resolvedIds = new Set(resolved.map(o => String(o.id)));

                    requestedIds.forEach(id => {
                        if (!resolvedIds.has(id)) {
                            const truncatedId = id.length > 64 ? `${id.slice(0, 64)}…` : id;
                            Sentry.captureMessage(`GridSettingsService: could not resolve ${property}:${truncatedId} for a filter chip`, 'warning');
                        }
                    });

                    if (resolved.length === 0) return;

                    const existingIds = new Set((this.objectOptions[property] ?? []).map(o => String(o.id)));
                    const newEntries = resolved.filter(o => !existingIds.has(String(o.id)));
                    if (newEntries.length === 0) return;

                    this.objectOptions[property] = [...(this.objectOptions[property] ?? []), ...newEntries];

                    const existingFallbackIds = new Set((this.resolvedFallbacks[property] ?? []).map(o => String(o.id)));
                    const newFallbacks = newEntries.filter(o => !existingFallbackIds.has(String(o.id)));
                    this.resolvedFallbacks[property] = [...(this.resolvedFallbacks[property] ?? []), ...newFallbacks];
                });
            }),
            map(() => undefined)
        );
    }

    buildFilters(filters: ColumnFilter[]): Record<string, string> {
        const operatorMap: Record<string, OperatorTypeEnum> = {
            [FilterOperator.Equal]: OperatorTypeEnum.Equal,
            [FilterOperator.LessThan]: OperatorTypeEnum.LessThan,
            [FilterOperator.GreaterThan]: OperatorTypeEnum.GreaterThan,
            [FilterOperator.LessThanOrEqual]: OperatorTypeEnum.LessThanOrEqual,
            [FilterOperator.GreaterThanOrEqual]: OperatorTypeEnum.GreaterThanOrEqual,
            [FilterOperator.Contains]: OperatorTypeEnum.Contains,
            [FilterOperator.StartsWith]: OperatorTypeEnum.StartsWith,
            [FilterOperator.IsEmpty]: OperatorTypeEnum.Equal,
            [FilterOperator.IsNotEmpty]: OperatorTypeEnum.NotEqual,
            [FilterOperator.RelativeToToday]: OperatorTypeEnum.Equal,
        };

        const isoFormat = this.dateFormatService.isoDateTimeFormat;
        const toDate = (v: any) => v instanceof Date ? v : this.dateFormatService.convertStringToDate(v);

        const result = filters.filter(f => !f.inherited).reduce<Record<string, string>>((acc, filter) => {
            const { column, operator, valueArray, value } = filter;
            const field = column.property;

            if (column.type === 'boolean') {
                acc[field] = value === true || value === 'true' ? 'true' : 'false';

            } else if (column.type === 'date') {

                const createRangeFilter = (start: Date, end: Date) => {
                    acc[buildOperatorKey(OperatorTypeEnum.GreaterThanOrEqual, field)] = JSON.stringify({
                        Field: field,
                        Condition: OperatorTypeEnum.GreaterThanOrEqual.toString(),
                        Value: this.dateFormatService.formatDateTimeWithTimeZoneToString(start, isoFormat),
                    });
                    acc[buildOperatorKey(OperatorTypeEnum.LessThanOrEqual, field)] = JSON.stringify({
                        Field: field,
                        Condition: OperatorTypeEnum.LessThanOrEqual.toString(),
                        Value: this.dateFormatService.formatDateTimeWithTimeZoneToString(end, isoFormat),
                    });
                };

                if (operator === FilterOperator.Between && valueArray?.length === 2) {
                    const start = this.dateFormatService.getStartOfDay(toDate(valueArray[0]));
                    const end = this.dateFormatService.getEndOfDay(toDate(valueArray[1]));
                    createRangeFilter(start, end);

                } else if (operator === FilterOperator.RelativeToToday) {
                    const today = new Date();
                    createRangeFilter(this.dateFormatService.getStartOfDay(today), this.dateFormatService.getEndOfDay(today));

                } else if (operator === FilterOperator.Equal) {
                    if (value) {
                        const start = this.dateFormatService.getStartOfDay(toDate(value));
                        const end = this.dateFormatService.getEndOfDay(toDate(value));
                        createRangeFilter(start, end);
                    }

                } else if (operator in operatorMap) {
                    const opType = operatorMap[operator];
                    acc[buildOperatorKey(opType, field)] = JSON.stringify({
                        Field: field,
                        Condition: opType.toString(),
                        Value: value ? this.dateFormatService.formatDateTimeWithTimeZoneToString(toDate(value), isoFormat) : '',
                    });
                }

            } else if (column.type === 'number' && !this.hasValidObjectMap(column.objectMap)) {
                const hasValue = operator === FilterOperator.Between
                    ? valueArray?.length === 2
                    : value != null && value !== '';
                if (!hasValue && operator !== FilterOperator.IsEmpty && operator !== FilterOperator.IsNotEmpty) {
                    return acc;
                }
                const transform = column.valueOutputTransformRule;
                const applyUnitAndTransform = (val: number): number => {
                    let convertedValue = this.unitFilterService.isUnitAwareColumn(field)
                        ? this.unitFilterService.convertFilterValueToBaseUnit(field, val, filter.selectedUnit)
                        : val;
                    return applyNumberTransform(transform, convertedValue);
                };

                const linkedInfo = filter.selectedUnit != null
                    ? this.unitFilterService.getLinkedFieldInfo(field)
                    : null;
                const linkedProps = linkedInfo
                    ? { LinkedField: linkedInfo.linkedField, LinkedValue: filter.selectedUnit!.toString() }
                    : {};

                if (operator === FilterOperator.Between && valueArray?.length === 2) {
                    acc[buildOperatorKey(OperatorTypeEnum.GreaterThanOrEqual, field)] = JSON.stringify({
                        Field: field,
                        Condition: OperatorTypeEnum.GreaterThanOrEqual.toString(),
                        Value: applyUnitAndTransform(Number(valueArray[0])).toString(),
                        ...(filter.selectedUnit != null && { SelectedUnit: filter.selectedUnit }),
                        ...linkedProps,
                    });
                    acc[buildOperatorKey(OperatorTypeEnum.LessThanOrEqual, field)] = JSON.stringify({
                        Field: field,
                        Condition: OperatorTypeEnum.LessThanOrEqual.toString(),
                        Value: applyUnitAndTransform(Number(valueArray[1])).toString(),
                        ...(filter.selectedUnit != null && { SelectedUnit: filter.selectedUnit }),
                        ...linkedProps,
                    });
                } else if (operator === FilterOperator.Equal) {
                    acc[buildOperatorKey(OperatorTypeEnum.Equal, field)] = JSON.stringify({
                        Field: field,
                        Condition: OperatorTypeEnum.Equal.toString(),
                        Value: applyUnitAndTransform(Number(value)).toString(),
                        ...(filter.selectedUnit != null && { SelectedUnit: filter.selectedUnit }),
                        ...linkedProps,
                    });
                } else if (operator in operatorMap) {
                    const opType = operatorMap[operator];
                    const isEmptyCheck = operator === FilterOperator.IsEmpty || operator === FilterOperator.IsNotEmpty;
                    acc[buildOperatorKey(opType, field)] = JSON.stringify({
                        Field: field,
                        Condition: opType.toString(),
                        Value: isEmptyCheck ? null : applyUnitAndTransform(Number(value)).toString(),
                        ...(filter.selectedUnit != null && { SelectedUnit: filter.selectedUnit }),
                        ...linkedProps,
                    });
                }
            } else if (operator === FilterOperator.Equal) {
                acc[field] = valueArray?.join(',') ?? String(value ?? '');
            } else if (operator === FilterOperator.Contains || operator === FilterOperator.StartsWith) {
                const opType = operatorMap[operator];
                acc[buildOperatorKey(opType, field)] = JSON.stringify({
                    Field: field,
                    Condition: opType.toString(),
                    Value: valueArray?.join(',') ?? String(value ?? ''),
                });
            } else if (
                operator === FilterOperator.IsEmpty ||
                operator === FilterOperator.IsNotEmpty
            ) {
                const opType = operatorMap[operator];
                acc[buildOperatorKey(opType, field)] = JSON.stringify({
                    Field: field,
                    Condition: opType.toString(),
                    Value: null,
                });
            }

            return acc;
        }, {});

        return result;
    }

    loadFiltersFromSettings(view: GridView | null, columns: DisplayGridColumnSettings[]): ColumnFilter[] {
        const filters = view?.settingsJson?.filters;
        if (!filters) return [];

        const parsedFilters = Object.entries(filters)
            .map(([_, raw]) => {
                try {
                    const parsed = JSON.parse(raw);
                    return typeof parsed === 'object' && parsed?.Field && parsed?.Condition !== undefined
                        ? { ...parsed, Condition: Number(parsed.Condition) }
                        : null;
                } catch {
                    return null;
                }
            })
            .filter(Boolean) as { Field: string, Condition: number, Value: string, SelectedUnit?: number }[];

        const filtersByField = parsedFilters.reduce((acc, filter) => {
            if (!acc[filter.Field]) acc[filter.Field] = [];
            acc[filter.Field].push(filter);
            return acc;
        }, {} as Record<string, typeof parsedFilters>);

        const processedFields = new Set<string>();

        const result = Object.entries(filters).flatMap(([key, raw]) => {
            try {
                const lowerKey = key.toLowerCase();
                const isComplex = lowerKey.startsWith('operatortypeenum.');
                const parsed = isComplex ? JSON.parse(raw) : { Field: key, Value: raw };
                const field = parsed.Field;

                if (processedFields.has(field)) return [];

                const column = columns.find(c => c.property === field) ||
                    columns.find(c => c.property.toLowerCase() === field.toLowerCase());
                if (!column) {
                    return [];
                }

                if (column.type === 'date' && isComplex) {
                    processedFields.add(field);

                    const fieldFilters = filtersByField[field] ?? [];
                    const gte = fieldFilters.find(f => f.Condition === OperatorTypeEnum.GreaterThanOrEqual);
                    const lte = fieldFilters.find(f => f.Condition === OperatorTypeEnum.LessThanOrEqual);

                    if (gte && lte) {
                        const start = this.dateFormatService.convertStringToDate(gte.Value);
                        const end = this.dateFormatService.convertStringToDate(lte.Value);
                        const isSingleDay = Math.abs(end.getTime() - start.getTime()) === 86399999;

                        return isSingleDay
                            ? [this.buildFilter(column, FilterOperator.Equal, start, [])]
                            : [this.buildFilter(column, FilterOperator.Between, '', [start, end])];
                    } else {
                        return fieldFilters.map(f => {
                            const operator = this.mapOperatorTypeEnumToFilter(f.Condition);
                            const value = this.dateFormatService.convertStringToDate(f.Value);
                            return this.buildFilter(column, operator, value, []);
                        });
                    }
                }

                if ((column.type === 'string' || !column.type) && isComplex) {
                    processedFields.add(field);

                    const fieldFilters = filtersByField[field] ?? [];
                    return fieldFilters.map(f => {
                        const operator = this.mapOperatorTypeEnumToFilter(f.Condition);
                        const value = f.Value;
                        const valueArray = value?.split?.(',')?.filter(Boolean) ?? [];

                        return this.buildFilter(column, operator, value, valueArray);
                    });
                }

                if (column.type === 'number' && isComplex) {
                    processedFields.add(field);

                    const fieldFilters = filtersByField[field] ?? [];
                    const savedUnit = fieldFilters[0]?.SelectedUnit != null ? Number(fieldFilters[0].SelectedUnit) : undefined;

                    const applyTransformAndUnit = (val: number): number => {
                        let transformedValue = applyNumberTransform(column.valueInputTransformRule, val);
                        return this.unitFilterService.isUnitAwareColumn(field)
                            ? this.unitFilterService.convertFilterValueFromBaseUnit(field, transformedValue, savedUnit)
                            : transformedValue;
                    };

                    const gte = fieldFilters.find(f => f.Condition === OperatorTypeEnum.GreaterThanOrEqual);
                    const lte = fieldFilters.find(f => f.Condition === OperatorTypeEnum.LessThanOrEqual);
                    if (gte && lte) {
                        const filter = this.buildFilter(column, FilterOperator.Between, '', [
                            applyTransformAndUnit(Number(gte.Value)),
                            applyTransformAndUnit(Number(lte.Value))
                        ]);
                        filter.selectedUnit = savedUnit;
                        return [filter];
                    }
                    return fieldFilters.map(f => {
                        const hasNumericValue = f.Value != null && String(f.Value).trim() !== '' && !Number.isNaN(Number(f.Value));
                        const operator = !hasNumericValue && f.Condition === OperatorTypeEnum.Equal
                            ? FilterOperator.IsEmpty
                            : this.mapOperatorTypeEnumToFilter(f.Condition);
                        const value = hasNumericValue ? applyTransformAndUnit(Number(f.Value)) : null;
                        const filter = this.buildFilter(column, operator, value, []);
                        filter.selectedUnit = savedUnit;
                        return filter;
                    });
                }

                let operator: FilterOperator = FilterOperator.Equal;
                let value = parsed.Value;
                let valueArray: any[] = [];

                valueArray = value?.split?.(',')?.filter(Boolean) ?? [];
                if (column.enumMap || this.hasValidObjectMap(column.objectMap)) {
                    operator = FilterOperator.Equal;
                    if (column.enumMap) {
                        valueArray = valueArray.map((v: string) => Number(v));
                    }
                }
                if (column.type === 'boolean') {
                    value = value === 'true' || value === true;
                }

                return [this.buildFilter(column, operator, value, valueArray)];

            } catch (error) {
                return [];
            }
        });

        return result;
    }

    private mapOperatorTypeEnumToFilter(condition: number): FilterOperator {
        switch (condition) {
            case OperatorTypeEnum.Equal: return FilterOperator.Equal;
            case OperatorTypeEnum.LessThan: return FilterOperator.LessThan;
            case OperatorTypeEnum.LessThanOrEqual: return FilterOperator.LessThanOrEqual;
            case OperatorTypeEnum.GreaterThan: return FilterOperator.GreaterThan;
            case OperatorTypeEnum.GreaterThanOrEqual: return FilterOperator.GreaterThanOrEqual;
            case OperatorTypeEnum.NotEqual: return FilterOperator.IsNotEmpty;
            case OperatorTypeEnum.Contains: return FilterOperator.Contains;
            case OperatorTypeEnum.StartsWith: return FilterOperator.StartsWith;
            default: return FilterOperator.Equal;
        }
    }

    private buildFilter(column: DisplayGridColumnSettings, operator: FilterOperator, value: any, valueArray: any[] = []): ColumnFilter {
        return { column, operator, value, valueArray, isEditing: false, search: '' };
    }

    createFiltersFromAdditionalFilters(
        additionalFilters: Record<string, unknown>,
        columns: DisplayGridColumnSettings[],
        dateFilterColumnProperty?: string
    ): { filters: ColumnFilter[], visibilityChanged: boolean } {
        const filters: ColumnFilter[] = [];
        let visibilityChanged = false;

        for (const [key, value] of Object.entries(additionalFilters)) {
            if (key === DATE_FILTER_TYPE_ENUM && dateFilterColumnProperty) {
                const dateFilter = this.buildInheritedDateFilter(value, columns, dateFilterColumnProperty);
                if (dateFilter) {
                    if (!dateFilter.column.visible) {
                        dateFilter.column.visible = true;
                        visibilityChanged = true;
                    }
                    filters.push(dateFilter);
                }
                continue;
            }
            if (key.startsWith('OperatorTypeEnum')) {
                if (value != null && value !== '') {
                    let parsed: { Condition: string; Value: string; Field?: string } | null = null;
                    try {
                        parsed = JSON.parse(value as string) as { Condition: string; Value: string; Field?: string };
                    } catch {
                        // Skip malformed filter values
                    }

                    const underscoreIdx = key.indexOf('_');
                    const fieldKey = parsed?.Field ?? (underscoreIdx !== -1 ? key.substring(underscoreIdx + 1) : null);
                    if (parsed && fieldKey) {
                        const col = columns.find(c =>
                            c.property.toLowerCase() === fieldKey.toLowerCase() ||
                            (c.nestedPath && `${c.nestedPath}.${c.property}`.toLowerCase() === fieldKey.toLowerCase())
                        );
                        if (col) {
                            if (!col.visible) {
                                col.visible = true;
                                visibilityChanged = true;
                            }
                            const operator = this.mapOperatorTypeEnumToFilter(Number(parsed.Condition));
                            filters.push(this.buildFilter(col, operator, parsed.Value, [parsed.Value]));
                        }
                    }
                }
                continue;
            }
            if (value == null || value === '') continue;

            const matchingColumn = columns.find(col =>
                col.property.toLowerCase() === key.toLowerCase() ||
                (col.nestedPath && `${col.nestedPath}.${col.property}`.toLowerCase() === key.toLowerCase())
            );

            if (!matchingColumn) continue;

            if (!matchingColumn.visible) {
                matchingColumn.visible = true;
                visibilityChanged = true;
            }

            const isObject = this.hasValidObjectMap(matchingColumn.objectMap);
            const isEnum = !!matchingColumn.enumMap;

            filters.push(this.buildFilter(
                matchingColumn,
                FilterOperator.Equal,
                (isObject || isEnum) ? undefined : String(value),
                (isObject || isEnum) ? this.splitMultiValue(String(value)) : undefined
            ));
        }

        return { filters, visibilityChanged };
    }

    createDrillDownFilterChips(
        additionalFilters: Record<string, unknown>,
        labels: Record<string, string>
    ): DrillDownFilterChip[] {
        return Object.entries(additionalFilters)
            .filter(([key, value]) => value != null && value !== '' && Object.hasOwn(labels, key))
            .map(([key]) => ({ key, label: labels[key] }));
    }

    extractQuickFilterValues(source: Record<string, unknown>, quickFilters: QuickFilter[]): Record<string, string> {
        return quickFilters.reduce<Record<string, string>>((acc, quickFilter) => {
            const value = source[quickFilter.key];
            if (typeof value === 'string' && value.trim() !== '') {
                acc[quickFilter.key] = value;
            }
            return acc;
        }, {});
    }

    buildViewFilters(filters: ColumnFilter[], quickFilterValues: Record<string, string>): Record<string, string> {
        return { ...this.buildFilters(filters), ...quickFilterValues };
    }

    private splitMultiValue(value: string): string[] {
        const parts = value.split(',').map(part => part.trim()).filter(part => part.length > 0);
        return parts.length > 0 ? parts : [value];
    }

    private buildInheritedDateFilter(
        value: unknown,
        columns: DisplayGridColumnSettings[],
        dateFilterColumnProperty: string
    ): ColumnFilter | null {
        if (typeof value !== 'string') return null;
        let parsed: { startDate?: number; endDate?: number };
        try {
            parsed = JSON.parse(value) as { startDate?: number; endDate?: number };
        } catch {
            return null;
        }
        if (!parsed.startDate || !parsed.endDate) return null;

        const column = columns.find(c => c.property.toLowerCase() === dateFilterColumnProperty.toLowerCase());
        if (!column) return null;

        const displayColumn: DisplayGridColumnSettings = { ...column, type: 'date', isFilterable: false, hiddenFromFilter: true };

        const start = this.dateFormatService.convertTimestampToDate(parsed.startDate);
        const end = this.dateFormatService.convertTimestampToDate(parsed.endDate);
        return { ...this.buildFilter(displayColumn, FilterOperator.Between, '', [start, end]), inherited: true };
    }

    buildDateFilterAdditionalValue(filter: ColumnFilter): string | null {
        const toDate = (v: unknown): Date | null => {
            if (v instanceof Date) return v;
            if (typeof v === 'string' || typeof v === 'number') return this.dateFormatService.convertStringToDate(String(v));
            return null;
        };
        const arr = filter.valueArray;
        let start: Date | null = null;
        let end: Date | null = null;
        if (Array.isArray(arr) && arr.length === 2) {
            start = toDate(arr[0]);
            end = toDate(arr[1]);
        } else if (filter.value != null && filter.value !== '') {
            start = toDate(filter.value);
            end = start;
        }
        if (!start || !end) return null;
        return JSON.stringify({
            dateFilterType: 0,
            startDate: this.dateFormatService.convertDateToTimestamp(start),
            endDate: this.dateFormatService.convertDateToTimestamp(end),
            previousStartDate: 0,
            previousEndDate: 0,
        });
    }

    hasValidObjectMap(obj: any): boolean {
        return !!obj && Object.values(obj).some(v => v != null);
    }

    getFilteredOptions(filter: ColumnFilter, objectOptions: any, isEnum = false): Lookup[] {
        const options = isEnum ? filter.column.enumMap || [] : objectOptions[filter.column.property] || [];
        const search = filter.search?.toLowerCase();
        return !search ? options : options.filter(o => o.name.toLowerCase().includes(search));
    }

    isStringFilter(f: ColumnFilter, opts = false): boolean {
        const isStringColumn = f.column.type === 'string' || !f.column.type;
        const hasNoSpecialMaps = !this.hasValidObjectMap(f.column.objectMap) && !f.column.enumMap;

        if (!opts) {
            return isStringColumn && hasNoSpecialMaps;
        }

        const stringOperators = ['contains', 'startsWith', 'is', 'isEmpty', 'isNotEmpty'];
        const isStringOperator = stringOperators.includes(f.operator);

        return isStringColumn && hasNoSpecialMaps && isStringOperator;
    }

    isEnumFilter(f: ColumnFilter): boolean { return !!f.column.enumMap; }

    isObjectFilter(f: ColumnFilter): boolean { return this.hasValidObjectMap(f.column.objectMap); }

    isBooleanFilter(f: ColumnFilter): boolean { return f.column.type === 'boolean'; }

    isDateFilter(f: ColumnFilter): boolean { return f.column.type === 'date'; }

    isNumberFilter(f: ColumnFilter): boolean { return f.column.type === 'number'; }

    isUnitAwareFilter(filter: ColumnFilter): boolean {
        return this.unitFilterService.isUnitAwareColumn(filter.column.property);
    }

    getFilterUnitLabel(filter: ColumnFilter): string {
        return this.unitFilterService.getColumnUnitLabel(filter.column.property, filter.selectedUnit);
    }

    hasUnitOptions(filter: ColumnFilter): boolean {
        return this.unitFilterService.hasUnitOptions(filter.column.property);
    }

    getAvailableUnits(filter: ColumnFilter): UnitOption[] {
        return this.unitFilterService.getAvailableUnits(filter.column.property);
    }

    getDisplayValue(filter: ColumnFilter, showValue: boolean = true): string {
        const { value, valueArray, operator, column } = filter;
        if (operator === FilterOperator.IsEmpty) return showValue ? 'Empty' : '';
        if (operator === FilterOperator.IsNotEmpty) return showValue ? 'Not Empty' : '';
        if (this.isStringFilter(filter)) {
            if (!valueArray || valueArray.length === 0) return '';
            if (operator === FilterOperator.Contains) {
                return valueArray.map(text => `*${text}*`).join(', ');
            }
            if (operator === FilterOperator.StartsWith) {
                return valueArray.map(text => `${text}*`).join(', ');
            }
            return valueArray.join(', ');
        }
        if (this.isEnumFilter(filter)) return (valueArray || []).map(v => this.getEnumDisplayName(column.enumMap, Number(v))).join(', ');
        if (this.isObjectFilter(filter)) return (valueArray || []).map(v => this.getObjectDisplayName(column.property, String(v), this.objectOptions)).join(', ');
        if (this.isBooleanFilter(filter)) return value ? 'True' : 'False';
        if (this.isDateFilter(filter)) {
            const format = this.dateFormatService.userDateFormat;
            const toDate = (v: any) => v instanceof Date ? v : this.dateFormatService.convertStringToDate(v);
            const operatorSymbols = {
                [FilterOperator.LessThan]: '<',
                [FilterOperator.LessThanOrEqual]: '<=',
                [FilterOperator.GreaterThan]: '>',
                [FilterOperator.GreaterThanOrEqual]: '>=',
            };
            if (operator === FilterOperator.Between && valueArray?.length === 2) {
                return `${this.dateFormatService.formatDateTimeWithTimeZoneToString(toDate(valueArray[0]), format)} - ${this.dateFormatService.formatDateTimeWithTimeZoneToString(toDate(valueArray[1]), format)}`;
            }
            if (operator === FilterOperator.RelativeToToday || value === 'today') {
                return 'Today';
            }
            if (operatorSymbols[operator] && value) {
                const dateStr = this.dateFormatService.formatDateTimeWithTimeZoneToString(toDate(value), format);
                return showValue ? `${operatorSymbols[operator]} ${dateStr}` : dateStr;
            }
            if (value) {
                return this.dateFormatService.formatDateTimeWithTimeZoneToString(toDate(value), format);
            }
            return '';
        }
        if (this.isNumberFilter(filter)) {
            const operatorSymbols = {
                [FilterOperator.LessThan]: '<',
                [FilterOperator.LessThanOrEqual]: '<=',
                [FilterOperator.GreaterThan]: '>',
                [FilterOperator.GreaterThanOrEqual]: '>=',
            };
            const unitLabel = this.getFilterUnitLabel(filter);
            const unitSuffix = unitLabel ? ` ${unitLabel}` : '';

            if (operator === FilterOperator.Between && valueArray?.length === 2) {
                return `${Number(valueArray[0])} - ${Number(valueArray[1])}${unitSuffix}`;
            }
            if (Array.isArray(valueArray) && valueArray.length > 0 && operator === FilterOperator.Contains) {
                return valueArray.map(v => Number(v)).join(', ') + unitSuffix;
            }
            if (operatorSymbols[operator] && value != null && value !== '') {
                return showValue ? `${operatorSymbols[operator]} ${Number(value)}${unitSuffix}` : `${Number(value)}${unitSuffix}`;
            }
            if (value != null && value !== '') {
                return `${Number(value)}${unitSuffix}`;
            }
            return '';
        }
        return '';
    }

    getEnumDisplayName(map: { value: number, name: string }[] | undefined, value: number): string {
        return map?.find(m => m.value === value)?.name ?? '';
    }

    getObjectDisplayName(prop: string, id: number | string, objOptions: Record<string, Lookup[]>): string {
        const match = objOptions[prop]?.find(o => String(o.id) === String(id));
        return match ? match.name : this.translateService.getDynamicTranslation('Unavailable');
    }

    getColumnTypeIcon(filter: ColumnFilter): string {
        const t = filter.column.type;
        if (this.isEnumFilter(filter) || this.isObjectFilter(filter)) return 'fa fa-list-ul';
        if (this.isBooleanFilter(filter)) return 'fa fa-check';
        if (t === 'string') return 'fa fa-font';
        if (t === 'number') return 'fa fa-hashtag';
        if (t === 'date') return 'fa fa-calendar';
        return '';
    }
}

function applyNumberTransform(transform: ((value: number) => number) | undefined, v: number): number {
    return typeof transform === 'function' ? transform(v) : v;
}
